import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { useLocation } from 'wouter';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Button } from './button';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

dayjs.extend(relativeTime);

interface NotificationBellProps {
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className }) => {
  const { notifications, unreadCount, markAsRead, fetchNotifications } = useNotifications();
  const [open, setOpen] = useState(false);
  const [_, navigate] = useLocation();

  // Refresh notifications when dialog is opened
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  const handleNotificationClick = (id: string, isRead: boolean, actionUrl?: string) => {
    setOpen(false);
    
    // Mark as read if not already read
    if (!isRead) {
      markAsRead([id]);
    }
    
    // Navigate to notification page or action URL
    if (actionUrl) {
      // Handle special cases for friend requests
      const notification = notifications.find(n => n.id === id);
      if (notification && notification.type.includes('friend_request') && actionUrl === '/friends') {
        navigate('/messages');
      } else {
        navigate(actionUrl);
      }
    } else {
      navigate('/notifications');
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  // Get color for notification type
  const getNotificationTypeColor = (type: string) => {
    switch (type) {
      case 'friend_request':
      case 'friend_request_accepted':
        return 'bg-blue-500';
      case 'game_invite':
      case 'game_turn':
        return 'bg-green-500';
      case 'game_completed':
      case 'round_completed':
        return 'bg-purple-500';
      case 'achievement':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Make sure notifications is an array before sorting
  const recentNotifications = Array.isArray(notifications) 
    ? [...notifications]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5) // Get 5 most recent
    : [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10 relative", className)}
          title="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 transform translate-x-1/4 -translate-y-1/4 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DialogTrigger>
      
      <DialogContent className="w-full max-w-md p-0 sm:max-w-md overflow-hidden">
        <DialogHeader className="p-3 border-b border-border flex justify-between items-center">
          <DialogTitle className="text-md font-semibold">Notifications</DialogTitle>
          {unreadCount > 0 && (
            <span className="bg-primary/10 text-primary text-xs rounded-full px-2 py-0.5">
              {unreadCount} new
            </span>
          )}
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          {recentNotifications.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              <p>No notifications yet</p>
            </div>
          ) : (
            <div>
              {recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification.id, notification.isRead, notification.actionUrl)}
                  className={`p-4 border-b border-border last:border-b-0 hover:bg-accent/50 cursor-pointer flex items-start gap-3 ${!notification.isRead ? 'bg-accent/20' : ''}`}
                >
                  <div className={`${getNotificationTypeColor(notification.type)} w-3 h-3 rounded-full mt-1 flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium text-sm truncate">{notification.title}</h4>
                      <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                        {dayjs(notification.createdAt).fromNow()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {notification.message}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-3 border-t border-border bg-background/50">
          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            onClick={handleViewAll}
          >
            View all notifications
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationBell;